import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { trapTab } from '../ui/focus';
import { NumberInput } from '../ui/NumberInput';
import { formatDecimal, parseScaled } from '../ui/numbers';
import type { Overtime } from './types';
import { DialogLayer } from '../ui/Dialog';
import { sameValues, useOpening, useUnsavedGuard } from '../ui/unsaved';

/**
 * "Nadgodziny" (F39, D16): a person of the crew, hours (3,5), multiplier ×1,5 or ×2, a description. The event's
 * manager uses it too: nothing here shows a rate or an amount. With `editing` it changes that entry.
 */
export function OvertimeDialog({
  eventId,
  people,
  editing,
  onClose,
  onSaved,
}: {
  eventId: string;
  people: Array<{ personId: string; personName: string }>;
  editing?: Overtime | undefined;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [personId, setPersonId] = useState(editing?.personId ?? people[0]?.personId ?? '');
  const [hours, setHours] = useState(editing ? formatDecimal(editing.hoursMilli / 1000) : '');
  const [multiplierBp, setMultiplierBp] = useState(editing?.multiplierBp ?? 15000);
  const [description, setDescription] = useState(editing?.description ?? '');
  const [local, setLocal] = useState('');
  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      editing ? api(`/api/settlements/overtime/${editing.id}`, { method: 'PATCH', body: { ...body, version: editing.version } }) : api(`/api/settlements/events/${eventId}/overtime`, { method: 'POST', body: { personId, ...body } }),
    onSuccess: onSaved,
  });
  /** What to send, or null with the reason shown. */
  function bodyOf(): Record<string, unknown> | null {
    const hoursMilli = parseScaled(hours, 3);
    if (hoursMilli === null || hoursMilli <= 0) return (setLocal('Podaj liczbę godzin, np. 3,5'), null);
    if (!editing && !personId) return (setLocal('Wybierz osobę'), null);
    setLocal('');
    return editing
      ? {
          ...(hoursMilli !== editing.hoursMilli ? { hoursMilli } : {}),
          ...(multiplierBp !== editing.multiplierBp ? { multiplierBp } : {}),
          ...(description.trim() !== editing.description ? { description: description.trim() } : {}),
        }
      : { hoursMilli, multiplierBp, description: description.trim() };
  }
  function submit(e: FormEvent) {
    e.preventDefault();
    const body = bodyOf();
    if (!body) return;
    if (Object.keys(body).length === 0) return onClose();
    save.mutate(body);
  }
  // owner 2026-10-23 21:14: leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const opening = useOpening({ personId, hours, multiplierBp, description });
  const guardRef = useUnsavedGuard({
    dirty: !sameValues({ personId, hours, multiplierBp, description }, opening),
    save: async () => {
      const body = bodyOf();
      if (!body) return false;
      if (Object.keys(body).length > 0) await save.mutateAsync(body);
      return true;
    },
  });
  const error = save.error instanceof ApiError ? (save.error.status === 409 ? 'Ktoś w tym czasie zmienił te nadgodziny. Zamknij i otwórz ponownie.' : (fieldIssues(save.error)[0]?.message ?? save.error.message)) : save.error ? 'Nie udało się zapisać' : '';
  return (
    <div className="dialog-backdrop">
      <form
        ref={guardRef}
        className="dialog panel form"
        role="dialog"
        aria-modal="true"
        aria-labelledby="overtime-title"
        data-feature-id="settlements.overtime"
        onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
        <DialogLayer onClose={onClose} busy={save.isPending} />
        <h2 id="overtime-title">Nadgodziny</h2>
        <label className="field">
          <span>Osoba</span>
          <select value={personId} onChange={(e) => setPersonId(e.target.value)} disabled={Boolean(editing)}>
            {editing && !people.some((p) => p.personId === editing.personId) && <option value={editing.personId}>{editing.personName}</option>}
            {people.map((p) => (
              <option key={p.personId} value={p.personId}>
                {p.personName}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Godziny</span>
          <NumberInput autoFocus value={hours} onValueChange={setHours} placeholder="np. 3,5" />
        </label>
        <fieldset className="field">
          <legend>Przelicznik</legend>
          <div className="segment">
            {[15000, 20000].map((bp) => (
              <label key={bp} className={multiplierBp === bp ? 'on' : undefined}>
                <input type="radio" name="multiplier" checked={multiplierBp === bp} onChange={() => setMultiplierBp(bp)} /> × {formatDecimal(bp / 10000)}
              </label>
            ))}
          </div>
        </fieldset>
        <label className="field">
          <span>Opis</span>
          <input value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} placeholder="np. demontaż po północy" autoComplete="off" />
        </label>
        {(local || error) && (
          <p className="alert" role="alert">
            {local || error}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" disabled={save.isPending}>
            {save.isPending ? 'Zapisywanie…' : 'Zapisz'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
